/**
 * React Print Service
 * API client for React print renderer integration
 * Separated from printService.ts to keep both under 500 lines
 */

import type { PrintDocumentData, TemplateConfig } from '@zaviago/print-renderer';
import { ZAVIAGO_SIGNATURE_IMAGE } from '@zaviago/print-renderer';
import { logger } from "@/lib/utils/logger";
import { NetworkError } from "@/lib/errors/ErrorTypes";
import { useAuthStore, ensureFreshToken } from "@/lib/store/authStore";

/** Build API URL for a given module + method on the Tenant Server. */
function getTenantApiUrl(modulePath: string, method: string): string {
  const { tenantServerUrl, authMode } = useAuthStore.getState();

  if (authMode === "control_plane" && tenantServerUrl) {
    return `${tenantServerUrl}/api/method/zaviago_backend.api.${modulePath}.${method}`;
  }

  const baseUrl = import.meta.env.VITE_FRAPPE_BASE_URL;
  const apiUrl = import.meta.env.VITE_FRAPPE_API_URL;
  const baseOrigin = baseUrl || (apiUrl?.replace(/\/api\/?$/, "")) || "";
  return `${baseOrigin}/api/method/zaviago_backend.api.${modulePath}.${method}`;
}

/** Auth headers — reuses same pattern as printService.ts */
function getAuthHeaders(): { headers: HeadersInit; credentials: RequestCredentials } {
  const { authMode, cpToken, currentTenant } = useAuthStore.getState();

  if (authMode === "control_plane" && cpToken) {
    return {
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": `Bearer ${cpToken}`,
        "X-Tenant-ID": currentTenant || "",
      },
      credentials: "omit" as RequestCredentials,
    };
  }

  return {
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include" as RequestCredentials,
  };
}

/**
 * Fetch normalized PrintDocumentData for React renderer.
 * Calls get_print_data on the Tenant Server.
 */
export async function getPrintData(
  doctype: string,
  name: string,
  company?: string,
): Promise<PrintDocumentData> {
  await ensureFreshToken();

  const url = getTenantApiUrl("print", "get_print_data");
  const { headers, credentials } = getAuthHeaders();

  const response = await fetch(url, {
    method: "POST",
    credentials,
    headers,
    body: JSON.stringify({ doctype, name, ...(company ? { company } : {}) }),
  });

  if (!response.ok) {
    throw new NetworkError(`Print data request failed with status ${response.status}`);
  }

  const result = await response.json();
  const data: PrintDocumentData = result.message || result;

  // Inject default signature block if backend didn't provide one
  if (!data.signatures || data.signatures.length === 0) {
    data.signatures = [
      { label: 'Authorized Signature', image: ZAVIAGO_SIGNATURE_IMAGE },
    ];
  }

  return data;
}

/**
 * Get template style configuration.
 * Returns saved TemplateConfig or empty object (components use DEFAULT_TEMPLATE_CONFIG).
 */
export async function getTemplateConfig(
  templateName: string,
): Promise<Partial<TemplateConfig>> {
  await ensureFreshToken();

  const url = getTenantApiUrl("print_template_api", "get_template_config");
  const { headers, credentials } = getAuthHeaders();

  const response = await fetch(url, {
    method: "POST",
    credentials,
    headers,
    body: JSON.stringify({ template_name: templateName }),
  });

  if (!response.ok) {
    logger.warn("Failed to fetch template config, using defaults");
    return {};
  }

  const result = await response.json();
  const payload = result.message || {};
  // Backend returns { config: {...} } — unwrap it
  return payload.config || payload;
}

/**
 * Save template style configuration.
 */
export async function saveTemplateConfig(
  templateName: string,
  styleConfig: Partial<TemplateConfig>,
): Promise<void> {
  await ensureFreshToken();

  const url = getTenantApiUrl("print_template_api", "save_template_config");
  const { headers, credentials } = getAuthHeaders();

  const response = await fetch(url, {
    method: "POST",
    credentials,
    headers,
    body: JSON.stringify({ template_name: templateName, style_config: styleConfig }),
  });

  if (!response.ok) {
    throw new NetworkError(`Save template config failed with status ${response.status}`);
  }
}

/**
 * Save edited print document data back to ERPNext.
 * Maps the normalized PrintDocumentData back to the source document fields.
 */
export async function savePrintDocument(
  doctype: string,
  name: string,
  data: PrintDocumentData,
): Promise<void> {
  await ensureFreshToken();

  const url = getTenantApiUrl("print", "save_print_data");
  const { headers, credentials } = getAuthHeaders();

  const response = await fetch(url, {
    method: "POST",
    credentials,
    headers,
    body: JSON.stringify({ doctype, name, data }),
  });

  if (!response.ok) {
    const result = await response.json().catch(() => ({}));
    const msg = result?._server_messages
      ? JSON.parse(JSON.parse(result._server_messages)?.[0] ?? "{}").message
      : `Save failed with status ${response.status}`;
    throw new NetworkError(msg || `Save failed with status ${response.status}`);
  }
}

/**
 * Reset template style configuration to defaults.
 */
export async function resetTemplateConfig(
  templateName: string,
): Promise<void> {
  await ensureFreshToken();

  const url = getTenantApiUrl("print_template_api", "reset_template_config");
  const { headers, credentials } = getAuthHeaders();

  const response = await fetch(url, {
    method: "POST",
    credentials,
    headers,
    body: JSON.stringify({ template_name: templateName }),
  });

  if (!response.ok) {
    throw new NetworkError(`Reset template config failed with status ${response.status}`);
  }
}
